'use client'

import { useState, useEffect } from 'react'
import { useRouter, usePathname } from 'next/navigation'

interface NavbarProps {
  backLabel?: string
  backTo?: string
  title?: string
  logoUrl?: string | null
  showCart?: boolean
  rightContent?: React.ReactNode
  onCartToggle?: () => void
  cartItemCount?: number
  searchContent?: React.ReactNode
  showSignIn?: boolean
  centerContent?: React.ReactNode
  hideRightContent?: boolean
}

const Navbar = ({
  backLabel,
  backTo,
  title,
  logoUrl,
  showCart = true,
  rightContent,
  onCartToggle,
  cartItemCount = 0,
  searchContent,
  showSignIn = false,
  centerContent,
  hideRightContent = false,
}: NavbarProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const isOnAccountPages = pathname?.startsWith('/accounts')

  useEffect(() => {
    // Check the non-HttpOnly companion cookie to detect customer login state
    // Re-checks on every navigation so login/logout state is always current
    setIsLoggedIn(document.cookie.split(';').some(c => c.trim().startsWith('customer-logged-in=')))
  }, [pathname])

  return (
    <nav className="public-header sticky top-0 z-40 bg-surface border-b border-surface-border shadow-navbar" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <div className="max-w-page mx-auto px-4 lg:px-8">
        <div className="relative flex items-center justify-between h-14">
          <div className="flex items-center gap-3 shrink-0 relative z-10">
            {backLabel && (
              <button
                onClick={() => backTo ? router.push(backTo) : router.back()}
                className="flex items-center gap-1.5 text-text-secondary hover:text-text-primary transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {backLabel}
              </button>
            )}
            {!backLabel && logoUrl && (
              <a href="/" className="flex items-center">
                <img src={logoUrl} alt={title || 'Logo'} className="h-8 w-auto max-w-[160px] object-contain" />
              </a>
            )}
            {!backLabel && !logoUrl && title && (
              <a href="/" className="font-heading font-bold text-lg text-text-primary hover:text-primary transition-colors">
                {title}
              </a>
            )}
          </div>

          {/* Center slot: custom content or desktop search */}
          {centerContent ? (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="pointer-events-auto">
                {centerContent}
              </div>
            </div>
          ) : searchContent ? (
            <div className="hidden lg:flex absolute inset-0 items-center justify-center pointer-events-none">
              <div className="w-full max-w-xl px-4 pointer-events-auto">
                {searchContent}
              </div>
            </div>
          ) : null}

          <div className="flex items-center gap-2 shrink-0 relative z-10">
            {!hideRightContent && showSignIn && !isOnAccountPages && (
              isLoggedIn ? (
                <>
                  {/* Mobile: icon only */}
                  <a
                    href="/accounts"
                    className="sm:hidden p-2 text-text-secondary hover:text-text-primary hover:bg-surface-tertiary rounded-lg transition-colors"
                    aria-label="My Account"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </a>
                  {/* Desktop: icon + text */}
                  <a
                    href="/accounts"
                    className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-surface-tertiary rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    My Account
                  </a>
                </>
              ) : (
                <>
                  {/* Mobile: icon only */}
                  <a
                    href="/accounts/login"
                    className="sm:hidden p-2 text-text-secondary hover:text-text-primary hover:bg-surface-tertiary rounded-lg transition-colors"
                    aria-label="Sign in"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </a>
                  {/* Desktop: icon + text */}
                  <a
                    href="/accounts/login"
                    className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-text-secondary hover:text-text-primary hover:bg-surface-tertiary rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Sign in
                  </a>
                </>
              )
            )}
            {!hideRightContent && rightContent}
            {!hideRightContent && showCart && (
              <button
                onClick={onCartToggle}
                className="relative p-2 text-text-secondary hover:text-text-primary transition-colors rounded-lg hover:bg-surface-tertiary"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                {cartItemCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-primary text-text-inverse text-xs font-bold min-w-[18px] min-h-[18px] flex items-center justify-center rounded-full">
                    {cartItemCount}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  )
}

export default Navbar
